/**
 * CMS clinic hours → schema.org `OpeningHoursSpecification`.
 *
 * `CmsLocation.hours` is free text an editor types per clinic, and fifteen
 * years of different editors show in it: days arrive as `"Monday"`, `"Mon"`,
 * `"Mon – Fri"` (a range) or `"Mon, Tue & Thu"` (a list), sometimes with a
 * trailing space; times as `"08:00 – 17:00"`, `"07:00 - 17:00"`, `"08:00 -17:00"`
 * or a split shift, `"08:00 – 13:00 / 14:00 – 17:00"`. Both dash characters
 * (ASCII `-` and en dash `–`) are in live data.
 *
 * Google reads `openingHoursSpecification` as a local-business signal, so the
 * parse has to be strict rather than generous: an entry whose day or time we
 * cannot read with certainty is dropped, never guessed. Publishing the wrong
 * opening time for a blood-draw site is worse than publishing none.
 */

import type { OpeningHoursSpecification } from "schema-dts"
import type { CmsClinicHours } from "@/lib/cms"

/** Weekday order, used to keep a merged `dayOfWeek` list in calendar order. */
const WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const

type Weekday = (typeof WEEK)[number]

/** Every spelling of a day that appears in (or plausibly reaches) the CMS. */
const DAY_BY_TOKEN = new Map<string, Weekday>(
  WEEK.flatMap((day) => [
    [day.toLowerCase(), day] as const,
    [day.slice(0, 3).toLowerCase(), day] as const,
  ])
)

/** Both dashes live in the CMS data; treat them identically. */
const DASH = /\s*[–-]\s*/

function parseDays(raw: string): Weekday[] {
  const text = raw.trim().toLowerCase()
  if (!text) return []

  // "Mon – Fri" — a range. Split first, because a range and a list never
  // co-occur in one entry and the dash would otherwise survive into a token.
  const rangeParts = text.split(DASH)
  if (rangeParts.length === 2) {
    const from = DAY_BY_TOKEN.get(rangeParts[0].trim())
    const to = DAY_BY_TOKEN.get(rangeParts[1].trim())
    if (!from || !to) return []
    const start = WEEK.indexOf(from)
    const end = WEEK.indexOf(to)
    // A backwards range ("Fri – Mon") is an editor error, not a weekend
    // wrap-around we should invent a meaning for.
    if (end < start) return []
    return WEEK.slice(start, end + 1)
  }

  // "Mon, Tue & Thu" / "Tue & Wed" / "Monday" — a list of one or more days.
  const tokens = text
    .split(/[,&]|\band\b/)
    .map((t) => t.trim())
    .filter(Boolean)
  const days = tokens.map((t) => DAY_BY_TOKEN.get(t))
  // One unreadable token invalidates the entry — a partial day list would
  // silently narrow the clinic's published hours.
  if (days.some((d) => d === undefined)) return []
  return days as Weekday[]
}

/** `{ opens, closes }` in the 24h `HH:MM` form schema.org expects. */
interface TimeRange {
  opens: string
  closes: string
}

function parseTime(raw: string): string | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(raw.trim())
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (hour > 23 || minute > 59) return null
  return `${String(hour).padStart(2, "0")}:${match[2]}`
}

function parseTimeRanges(raw: string): TimeRange[] {
  // A split shift is two ranges separated by "/" — each becomes its own
  // specification, which is exactly how schema.org models a lunch closure.
  const segments = raw
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean)
  if (!segments.length) return []

  const ranges = segments.map((segment) => {
    const parts = segment.split(DASH)
    if (parts.length !== 2) return null
    const opens = parseTime(parts[0])
    const closes = parseTime(parts[1])
    if (!opens || !closes || opens >= closes) return null
    return { opens, closes }
  })

  // As with days: an unreadable half means we don't know the shift pattern.
  if (ranges.some((r) => r === null)) return []
  return ranges as TimeRange[]
}

/**
 * Build the `openingHoursSpecification` array for one clinic.
 *
 * Entries sharing a time range are merged into a single specification with a
 * multi-day `dayOfWeek` — five identical weekday rows (the common CMS shape)
 * collapse to one node instead of five near-duplicates.
 *
 * Returns `[]` when nothing parses; callers omit the property entirely rather
 * than emitting an empty array.
 */
export function buildOpeningHoursSpecification(
  hours: CmsClinicHours[] | null | undefined
): OpeningHoursSpecification[] {
  if (!hours?.length) return []

  // Keyed by the time range so "Mon–Fri 08:00–17:00" written as five rows and
  // as one range row produce the same output.
  const byRange = new Map<string, { range: TimeRange; days: Set<Weekday> }>()

  hours.forEach((entry) => {
    const days = parseDays(entry.day)
    if (!days.length) return
    parseTimeRanges(entry.time).forEach((range) => {
      const key = `${range.opens}-${range.closes}`
      const existing = byRange.get(key)
      if (existing) {
        days.forEach((d) => existing.days.add(d))
      } else {
        byRange.set(key, { range, days: new Set(days) })
      }
    })
  })

  return Array.from(byRange.values())
    .map(({ range, days }) => {
      const weekdays = WEEK.filter((d) => days.has(d))
      return {
        spec: {
          "@type": "OpeningHoursSpecification" as const,
          dayOfWeek: weekdays.map((d) => `https://schema.org/${d}` as const),
          opens: range.opens,
          closes: range.closes,
        },
        // Sort key: the week block a specification starts in, then its
        // opening time — so a Mon–Fri block precedes a Saturday one, and a
        // split shift stays in chronological order. Map order alone would
        // leak CMS row order into the markup.
        firstDay: WEEK.indexOf(weekdays[0]),
      }
    })
    .sort((a, b) =>
      a.firstDay === b.firstDay
        ? a.spec.opens.localeCompare(b.spec.opens)
        : a.firstDay - b.firstDay
    )
    .map(({ spec }) => spec)
}
